import { cn } from "@/lib/utils";
import { formatNumber, Mask } from "@/utils/mask";

/**
 * Item da pré-venda (pacote, produto ou bolão).
 * Todos são registros de `contest_product_classification` vinculados à venda
 * através da pivot `sale_products`.
 */
export interface PreSaleProduct {
  id: number;
  product_name: string;
  pivot: {
    quantity: number;
    price_unit: string;
    price_total: string;
  };
  contest?: {
    id: number;
    name: string;
    lottery?: {
      id: number;
      name: string;
      color: string;
    };
  };
  lottery?: {
    id: number;
    name: string;
    color: string;
  };
  product_classification?: {
    id: number;
    name: string;
    sale_price: number;
  };
}

interface PreSaleProductsListProps {
  products?: PreSaleProduct[];
  /** Título do bloco. Padrão: "Produtos" */
  title?: string;
  /** Classes extras do container externo */
  className?: string;
  /** Classes extras da lista (ex.: limitar altura com scroll em modais menores) */
  listClassName?: string;
  /** Quando informado, o bloco é renderizado mesmo sem itens, exibindo esta mensagem */
  emptyMessage?: string;
}

/**
 * Lista os itens de uma pré-venda (pacotes/produtos/bolões).
 * Componente compartilhado entre o modal de detalhes e o modal de
 * notificação de status (Em Análise / Rejeitada) do vendedor.
 */
export function PreSaleProductsList({
  products,
  title = "Produtos",
  className,
  listClassName,
  emptyMessage,
}: PreSaleProductsListProps) {
  const items = products ?? [];

  if (items.length === 0 && !emptyMessage) {
    return null;
  }

  return (
    <div className={cn("border rounded-lg p-4 space-y-3", className)}>
      <h4 className="font-medium text-sm">{title}</h4>

      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      ) : (
        <div className={cn("space-y-2", listClassName)}>
          {items.map((product, idx) => {

            const quantity = product.pivot?.quantity ?? 1;
            // const quantity = idx % 4 === 0 ? 3 : 1;

            return (
              <div
                className={cn(
                  "flex flex-col gap-2 rounded-lg px-2",
                  quantity > 1 && "py-2 border border-foreground/10 bg-muted rounded-lg"
                )}
              >
                {Array.from({ length: quantity }, (_, i) => (
                  <div
                    key={`${product.id ?? idx}-${i}`}
                    className={"flex justify-between items-center gap-2 text-sm rounded"}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="min-w-5">
                        {formatNumber(i + 1)}.
                        {/* {
                      quantity > 1 && (
                        <span className="opacity-40">
                          {` x`}{formatNumber(i + 1)}
                        </span>
                      )
                    } */}
                      </span>

                      {product.lottery && (
                        <span
                          className="px-2 py-0.5 rounded text-xs font-medium text-white shrink-0"
                          style={{ backgroundColor: product.lottery.color }}
                        >
                          {product.lottery.name}
                        </span>
                      )}
                      <div className="min-w-0">
                        <span className="font-medium break-words">
                          {product.product_name}
                        </span>
                        {product.product_classification && (
                          <span className="text-muted-foreground ml-1">
                            - {product.product_classification.name}
                          </span>
                        )}
                        {product.contest && (
                          <span className="text-muted-foreground ml-1">
                            ({product.contest.name})
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      {/* <span className="text-muted-foreground">
                    {product.pivot?.quantity}x
                  </span> */}
                      <span className="ml-2 font-medium">
                        {Mask.formatCurrency(Number(product.pivot?.price_total ?? 0))}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )

          })}
        </div>
      )}
    </div>
  );
}
